<script setup>
import {
  computed,
  ref,
  reactive,
  watch,
} from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import useUserStore from '@/stores/userStore';
import {
  ADMINISTRATORS_VIEW,
  CHARGE_VIEW,
  DASHBOARD_VIEW,
  DEPARTMENTS_VIEW,
  IMAGES_VIEW,
  JOURNEYS_VIEW,
  KAKAO_PROFILES_VIEW,
  KAKAO_TEMPLATES_VIEW,
  OUTGOING_CALLS_VIEW,
  PROFILE_VIEW,
  REJECTION_NUMBERS_VIEW,
  UNSUBSCRIBE_CUSTOMERS_VIEW,
} from '@/config/permissions';
import ItemListGroup from '@/components/layout/menus/ItemListGroup.vue';
import ListItem from '@/components/layout/menus/ListItem.vue';

const { state } = storeToRefs(useUserStore());
const { t, locale } = useI18n();
const langType = ref(locale);
const route = useRoute();
const open = ref([]);

const scopeIncludes = (key) => {
  if (!key) {
    return true;
  }

  return state.value.scope.includes(key);
};

const leftMenu = reactive([
  {
    to: '/dashboard',
    prependIcon: 'mdi-poll',
    value: 'dashboard',
    className: 'menu__1li--dashboard',
    text: 'common.dashboard',
    name: ['Dashboard'],
    children: [],
    scope: DASHBOARD_VIEW,
  },
  {
    to: '/profile',
    value: 'profile',
    prependIcon: 'mdi-account-outline',
    className: 'menu__1li--member',
    text: 'common.profile',
    name: ['Profile'],
    children: [
      {
        value: 'subProfile',
        prependIcon: 'mdi-circle-small',
        to: '/profile',
        text: 'common.profile',
        name: ['Profile'],
        scope: PROFILE_VIEW,
      },
      {
        value: 'administrators',
        prependIcon: 'mdi-circle-small',
        to: '/administrators',
        text: 'common.administratorsSettings',
        name: ['Administrators Management'],
        scope: ADMINISTRATORS_VIEW,
      },
    ],
    scope: [
      ...PROFILE_VIEW,
      ...ADMINISTRATORS_VIEW,
    ],
  },
  {
    value: 'outgoingCalls',
    to: '/outgoing-calls',
    prependIcon: 'mdi-pencil-outline',
    className: 'menu__1li--info',
    text: 'common.registrationManagement',
    name: ['Outgoing Phone Management'],
    children: [
      {
        value: 'valueOutgoingCalls',
        prependIcon: 'mdi-circle-small',
        to: '/outgoing-calls',
        text: 'common.outgoingPhone',
        name: [
          'Outgoing Phone Management',
          'Outgoing Phone Management Change list order',
        ],
        scope: OUTGOING_CALLS_VIEW,
      },
      {
        value: 'kakaoProfiles',
        prependIcon: 'mdi-circle-small',
        to: '/kakao-profiles',
        text: 'common.kakaoProfile',
        name: [
          'Kakao Profile Management',
          'Kakao Profile Management Change list order',
        ],
        scope: KAKAO_PROFILES_VIEW,
      },
      {
        value: 'rejectionNumbers',
        prependIcon: 'mdi-circle-small',
        to: '/rejection-numbers',
        text: 'common.rejectPhone',
        name: [
          'Reject Phone Management',
          'Reject Phone Management Change list order',
        ],
        scope: REJECTION_NUMBERS_VIEW,
      },
      {
        value: 'unsubscribeCustomers',
        prependIcon: 'mdi-circle-small',
        to: '/unsubscribe-customers',
        text: 'common.unsubscribeCustomer',
        name: ['Unsubscribe Customer Care'],
        scope: UNSUBSCRIBE_CUSTOMERS_VIEW,
      },
      {
        value: 'departments',
        prependIcon: 'mdi-circle-small',
        to: '/departments',
        text: 'common.departmentInformation',
        name: [
          'Department Information Management',
          'Department Information Management Change list order',
        ],
        scope: DEPARTMENTS_VIEW,
      },
    ],
    scope: [
      ...OUTGOING_CALLS_VIEW,
      ...KAKAO_PROFILES_VIEW,
      ...REJECTION_NUMBERS_VIEW,
      ...UNSUBSCRIBE_CUSTOMERS_VIEW,
      ...DEPARTMENTS_VIEW,
    ],
  },
  {
    value: 'charge',
    to: '/charge?department=ALL',
    prependIcon: 'mdi-email-outline',
    className: 'menu__1li--user',
    text: 'common.userMenu',
    name: ['Charge'],
    children: [
      {
        value: 'subCharge',
        prependIcon: 'mdi-circle-small',
        to: '/charge?department=ALL',
        text: 'common.charge',
        name: ['Charge'],
        scope: CHARGE_VIEW,
      },
      {
        value: 'message-groups',
        to: '/message-groups',
        text: 'common.messageUsage',
        prependIcon: 'mdi-circle-small',
        name: [
          'Message Detail',
          'Message Group Activity List',
          'Message Group Version List',
          'Message Groups',
          'Message Group Messages',
        ],
        scope: JOURNEYS_VIEW,
      },
      {
        value: 'recipient-number',
        to: '/recipient-number',
        text: 'common.recipientNumber',
        prependIcon: 'mdi-circle-small',
        name: [
          'Recipient Number',
          'Recipient Number Message Detail',
        ],
        scope: JOURNEYS_VIEW,
      },
      {
        value: 'kakaoTemplates',
        to: '/kakao-templates',
        text: 'common.kakaoTemplate',
        prependIcon: 'mdi-circle-small',
        name: [
          'Kakao Template',
          'Kakao Template Management',
          'Kakao Template create form',
        ],
        scope: KAKAO_TEMPLATES_VIEW,
      },
      {
        value: 'images',
        prependIcon: 'mdi-circle-small',
        to: '/images',
        text: 'common.images',
        name: [
          'images',
        ],
        scope: IMAGES_VIEW,
      },
    ],
    scope: [
      ...CHARGE_VIEW,
      ...JOURNEYS_VIEW,
      ...KAKAO_TEMPLATES_VIEW,
      ...IMAGES_VIEW,
    ],
  },
]);

const nameObject = {};

const setNameObject = ({
  values,
  name,
  value,
  children = [],
}) => {
  const v = [...new Set([...values, value])];
  name.forEach((key) => {
    nameObject[key] = v;
  });

  children.forEach((item) => setNameObject({
    values: v,
    value: item.value,
    name: item.name,
    children: item.children,
  }));
};

leftMenu.forEach(({ value, name, children }) => {
  setNameObject({
    values: [],
    name,
    children,
    value,
  });
});

const displayLeftMenu = computed(() => leftMenu.map((item) => {
  const {
    to,
    text,
    name,
    children,
    scope,
    value,
    prependIcon,
  } = item;

  const filteredChildren = children.map((el) => ({
    to: el.to,
    value: el.value,
    prependIcon: el.prependIcon,
    title: t(el.text),
    name: el.name,
    children: [],
    allowRender: el.scope.some((k) => scopeIncludes(k)),
  }))
    .filter((el) => el.allowRender);

  return {
    to: filteredChildren?.[0]?.to ?? to,
    value,
    title: t(text),
    name,
    allowRender: scope.some((k) => scopeIncludes(k)),
    children: filteredChildren,
    prependIcon,
    components: filteredChildren.length ? ItemListGroup : ListItem,
  };
})
  .filter(({ allowRender }) => allowRender));

watch(langType, (v) => {
  locale.value = v;

  localStorage.setItem('locale', v);
  document.querySelector('html').setAttribute('lang', v);
}, { immediate: true });

const languages = computed(() => ([
  {
    value: 'ko',
    label: t('common.korean'),
  },
  {
    value: 'en',
    label: t('common.english'),
  },
]));

watch(route, (value) => {
  const values = nameObject[value.name] || [];

  values.forEach((v) => {
    if (!open.value.includes(v)) {
      open.value.push(v);
    }
  });
}, { immediate: true });

</script>
<template>
  <header class="header">
    <RouterLink
      to="/"
      class="logo"
    >
      <div
        class="logo__icon"
        aria-hidden="true"
      >
        <img
          src="@/assets/img/logo.svg"
          alt="logo"
        />
      </div>
    </RouterLink>
    <nav class="menu">
      <v-list
        v-model:opened="open"
      >
        <component
          v-for="(item, index) in displayLeftMenu"
          :is="item.components"
          :open="open"
          :key="`menu-${index}`"
          :prepend-icon="item.prependIcon"
          :to="item.to"
          :title="item.title"
          :name="item.name"
          :value="item.value"
          :children="item.children"
        />
      </v-list>
      <!-- <ul class="menu__1ul">
        <li
          v-for="item in displayLeftMenu"
          :key="`menu__1li${item.name}`"
          class="menu__1li"
          :class="item.classList"
        >
          <RouterLink
            :to="item.to"
            class="menu__1a"
          >
            <span
              class="menu__icon"
              aria-hidden="true"
            ></span>
            <span class="menu__text">
              {{ t(item.text) }}
            </span>
            <span
              class="menu__arrow"
              aria-hidden="true"
            ></span>
          </RouterLink>
          <ul class="menu__2ul">
            <li
              v-for="el in item.children"
              :key="`menu__2li${el.name}`"
              class="menu__2li"
              :class="el.classList"
            >
              <RouterLink
                :to="el.to"
                class="menu__2a"
              >
                <span class="menu__text">{{ t(el.text) }}</span>
              </RouterLink>
            </li>
          </ul>
        </li>
      </ul> -->
    </nav>
    <div class="etc-menu">
      <!-- <v-btn
        class="custom__button etc-menu__a justify-start"
        prepend-icon="mdi-download-outline"
        variant="text"
        size="small"
        @click="fieldClick(value)"
      >
        {{ t('common.solutionGuide') }}
      </v-btn> -->
      <v-select
        v-model="langType"
        class="etc-menu__select"
        :items="languages"
        item-title="label"
        item-value="value"
        :label="t('common.languageSelect')"
        hide-details
        :menu-props="{ contentClass: ['customer-setting-select'] }"
      >
        <template v-slot:selection="{ item }">
          <v-icon class="custom__icon">
            mdi-translate
          </v-icon>{{ item.title }}
        </template>
      </v-select>
    </div>
  </header>
</template>
